import { generatePath } from '@helpers/utils/paths/paths';
import { queryKeys } from '@terminal/routes/query-params';

interface SummaryCitation {
  id: number;
  kg_insight_id?: string;
  scenario_insight_id?: string;
}

const INLINE_CITATION_REGEX = /(?<![!\\])\[(\d+)\](?!\s*(?:\(|:))/g;

const getCitationInsightId = (citation: SummaryCitation): string | undefined =>
  citation.kg_insight_id ?? citation.scenario_insight_id;

const getDailyCitationHref = (
  citation: SummaryCitation
): string | undefined => {
  const insightId = getCitationInsightId(citation);

  if (!insightId) {
    return undefined;
  }

  return generatePath({
    basePath: '',
    params: { [queryKeys.insightId]: insightId },
  });
};

/**
 * Daily and monthly payloads use different scenario ID fields. The link only
 * carries the ID so the click handler can resolve the appropriate drawer.
 */
export const getDailyCitationInsightId = (
  href: string | null
): string | null => {
  if (!href?.startsWith('?')) {
    return null;
  }

  return new URLSearchParams(href.slice(1)).get(queryKeys.insightId);
};

export const linkDailySummaryCitations = (
  summary: string,
  citations: SummaryCitation[]
): string => {
  const hrefsByCitationId = new Map(
    citations.flatMap(citation => {
      const href = getDailyCitationHref(citation);

      return href ? [[citation.id, href] as const] : [];
    })
  );

  return summary.replace(INLINE_CITATION_REGEX, (match, id: string) => {
    const href = hrefsByCitationId.get(Number(id));
    return href ? `[\\[${id}\\]](${href})` : match;
  });
};

const BOLD_BEFORE_CITATION_REGEX = /\*\*([^*]+)\*\*(?=(?:\s*\[\d+\])+)/g;

export const stripDailySummaryCitations = (summary: string): string =>
  summary
    .replace(BOLD_BEFORE_CITATION_REGEX, '$1')
    .replace(INLINE_CITATION_REGEX, '')
    .replace(/\s+([,.;:!?])/g, '$1')
    .replace(/,(\s*,)+/g, ',')
    .replace(/\s{2,}/g, ' ')
    .trim();
